<!--C:\VUE3\platform\src\components\layouts\HeaderComponents.vue-->
<template>
  <Teleport to="body">
    <HeaderSettingPopup v-if="isHeaderSettingVisible" :country="currentCountry" :currency="currentCurrency"
      :language="language" @update-settings="onUpdateSettings" @close="isHeaderSettingVisible = false" />
  </Teleport>

  <header>
    <!-- ✅ SEO: aria-label 추가 (WCAG 2.4.1, 2.4.6) -->
    <nav class="navbar navbar-expand-lg navbar-light bg-white border-bottom shadow-sm mp-header" aria-label="메인 메뉴">
      <div class="container-fluid">
        <div class="m-0 p-0 d-flex align-items-center">
          <a class="navbar-brand" href="/" aria-label="엠피니티 홈으로 이동" @click.prevent="navigateTo('/')">
            <img src="@/assets/resources/img/headerlogo.png" alt="엠피니티(MPNITE) 로고" height="40" width="auto" />
          </a>
        </div>

        <button class="navbar-toggler border-0" type="button" aria-controls="mainNavbar" :aria-expanded="isMenuOpen"
          aria-label="메뉴 열기/닫기" @click="toggleMenu">
          <i v-if="isMenuOpen" class="bi bi-x-lg fs-1"></i>
          <i v-else class="bi bi-list fs-1"></i>
        </button>

        <div id="mainNavbar" class="collapse navbar-collapse">
          <ul class="navbar-nav me-auto mb-2 mb-lg-0">
            <li v-for="item in NAV_ITEMS" :key="item.path" class="nav-item">
              <a class="nav-link" :class="{ active: route.path === item.path }" :href="item.path"
                :aria-current="route.path === item.path ? 'page' : undefined" @click.prevent="navigateTo(item.path)">
                {{ item.label }}
              </a>
            </li>

            <li class="nav-item">
              <a
                class="nav-link"
                :href="BLOG_BASE_URL"
                @click="closeMenu"
              >
                Global Blog
              </a>
            </li>
          </ul>

          <ul class="navbar-nav ms-auto d-flex flex-row align-items-center">
            <li class="nav-item me-2">
              <button type="button" class="nav-link btn d-flex align-items-center" aria-label="언어 및 국가 설정 열기"
                @click="openLanguage">
                <i class="bi bi-globe me-1" aria-hidden="true"></i>
                <span class="d-none d-md-inline">
                  {{ displayCountry }}, {{ displayCurrency }}
                </span>
              </button>
            </li>

            <template v-if="isLogin">
              <AuthMenu :is-login="isLogin" :user="user" :language="language" :unread-count="unreadCount"
                @logout="logout" @switch-theme="toggleTheme" @open-admin="navigateTo('/admin')"
                @open-preferences="openLanguage" @open-language="openLanguage" @open-help="navigateTo('/board')"
                @open-notifications="goProtected({ name: 'myalarm' })" @cart="goProtected('/cartview')"
                @mypage="goProtected({ name: 'mymodel' })" @manage-account="goProtected({ name: 'myaccount' })"
                @add-account="goLogin()" />
            </template>

            <template v-else>
              <li class="nav-item">
                <a class="nav-link d-flex align-items-center" href="/login" @click.prevent="goLogin()">
                  <i class="bi bi-box-arrow-in-right me-1" aria-hidden="true"></i>
                  Login
                </a>
              </li>
            </template>

            <AppLauncher :items="APP_ITEMS" class="me-2" />
          </ul>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useMainStore } from "@/store/mainStore";
import Dropdown from "bootstrap/js/dist/dropdown";
import Collapse from "bootstrap/js/dist/collapse";

import HeaderSettingPopup from "@/components/layouts/HeaderSettingPopup.vue";
import AppLauncher from "@/components/layouts/AppLauncher.vue";
import AuthMenu from "@/components/layouts/AuthMenu.vue";

import userUtils from "@/common/userUtils";
import loginApi from "@/common/api/loginApi";
import { logDebug, logError } from "@/utils/logHelper";

import { useAlertsStore } from "@/store/notifications/useAlertsStore";
import { changeLanguage } from "@/i18n.js";

const { proxy } = getCurrentInstance();
const mainStore = useMainStore();
const alertsStore = useAlertsStore();
const unreadCount = computed(() => alertsStore.unreadCount);
const router = useRouter();
const route = useRoute();
const { t } = useI18n({ useScope: "global" });

const isHeaderSettingVisible = ref(false);
const isMenuOpen = ref(false);

const isLogin = computed(() => mainStore.isLogin);
const user = computed(() => mainStore.user);
const country = computed(() => mainStore.country);
const currency = computed(() => mainStore.currency);
const language = computed(() => mainStore.language);

const displayCountry = computed(() => {
  const countryMap = { KR: "KOREA", US: "USA", JP: "JAPAN" };
  return countryMap[country.value] || "Unknown";
});

const displayCurrency = computed(() => currency.value || "Unknown");
const currentCountry = computed(() => country.value);
const currentCurrency = computed(() => currency.value);

const NAV_ITEMS = [
  { label: "Quote", path: "/quotestart" },
  { label: "Machine", path: "/machine" },
  { label: "Materials", path: "/materialinfo" },
  { label: "Products", path: "/products" },
];

/** 별도 배포 블로그 사이트 (플랫폼 SPA 내부 라우트 아님) */
const BLOG_BASE_URL = process.env.VUE_APP_BLOG_URL || "https://mpnite.com/blog";

const APP_ITEMS = [
  { id: "quote", label: "Quote", icon: "bi-calculator", route: "/quotestart" },
  { id: "machine", label: "Machine", icon: "bi-cpu", route: "/machine" },
  { id: "service", label: "Service", icon: "bi-tools", route: "/serviceinfo" },
  {
    id: "materials",
    label: "Materials",
    icon: "bi-layers",
    route: "/materialinfo",
  },
  { id: "products", label: "Products", icon: "bi-bag", route: "/products" },
  { id: "board", label: "Guide", icon: "bi-book", route: "/board" },
  {
    id: "cart",
    label: "Cart",
    icon: "bi-cart",
    route: "/cartview",
    requiresAuth: true,
  },
  {
    id: "mypage",
    label: "My Page",
    icon: "bi-person-circle",
    route: { name: "mymodel" },
    requiresAuth: true,
  },
  {
    id: "init",
    label: "Reset",
    icon: "bi-bootstrap-reboot",
    route: "/initreset",
  },
];

function toggleMenu() {
  const navbar = document.getElementById("mainNavbar");
  if (navbar) {
    const bsCollapse = Collapse.getOrCreateInstance(navbar, { toggle: false });
    bsCollapse.toggle();
  }
}

function closeMenu() {
  const navbar = document.getElementById("mainNavbar");
  if (navbar && navbar.classList.contains("show")) {
    const bsCollapse = Collapse.getInstance(navbar);
    if (bsCollapse) bsCollapse.hide();
  }
  isMenuOpen.value = false;
}

function goProtected(target) {
  closeMenu();
  const nextPath =
    typeof target === "string" ? target : router.resolve(target).fullPath;
  if (mainStore.isLogin) {
    navigateTo(target);
  } else {
    router.push({ path: "/login", query: { next: nextPath } });
  }
}

function goLogin(next) {
  closeMenu();
  const cur = router.currentRoute.value;
  const fallback = "/";
  const nextPath =
    typeof next === "string"
      ? next
      : cur.path === "/login"
        ? cur.query.next || fallback
        : cur.fullPath;
  router.push({ path: "/login", query: { next: nextPath } });
}

function navigateTo(target) {
  closeMenu();
  if (typeof target === "string") {
    router.push({ path: target });
  } else {
    router.push(target);
  }
}

function openLanguage() {
  isHeaderSettingVisible.value = true;
}

function onUpdateSettings({
  country: newCountry,
  language: newLanguage,
  currency: newCurrency,
  locale: newLocale,
}) {
  mainStore.SET_COUNTRY(newCountry);
  mainStore.SET_CURRENCY(newCurrency);
  mainStore.SET_LANGUAGE(newLanguage);
  mainStore.SET_LOCALE(newLocale);
  localStorage.setItem("countryCode", newCountry);
  localStorage.setItem("currencyCode", newCurrency);
  localStorage.setItem("languageCode", newLanguage);
  localStorage.setItem("locale", newLocale);
  // i18n 메시지 트리는 ko | en | ja 만 존재. ja-JP 등으로 두면 폴백으로 한국어가 섞일 수 있음.
  changeLanguage(newLanguage);
}

async function logout() {
  try {
    logDebug("[logout] Attempting logout...");
    await loginApi.logout();
    userUtils.clearAccessToken();
    userUtils.clearRefreshToken();
    userUtils.clearUserInfo();
    mainStore.SET_IS_LOGIN(false);
    router.push({ path: "/" });
    proxy.$toast?.success(t("nav.alert.logout"));
  } catch (err) {
    logError("[logout] Unexpected error:", err);
  }
}

function toggleTheme() {
  const root = document.documentElement;
  const current = root.getAttribute("data-bs-theme") || "light";
  root.setAttribute("data-bs-theme", current === "dark" ? "light" : "dark");
}

watch(isLogin, (value) => {
  if (value) mainStore.REFRESH_USER_FROM_SESSION();
});

onMounted(() => {
  mainStore.REFRESH_USER_FROM_SESSION();
  document
    .querySelectorAll('[data-bs-toggle="dropdown"]')
    .forEach((el) => Dropdown.getOrCreateInstance(el, { display: "static" }));

  const navbar = document.getElementById("mainNavbar");
  if (navbar) {
    navbar.addEventListener("show.bs.collapse", () => {
      isMenuOpen.value = true;
    });
    navbar.addEventListener("hide.bs.collapse", () => {
      isMenuOpen.value = false;
    });
  }
});
</script>

<style scoped>
.mp-header {
  height: 60px;
  background-color: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.06) !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.mp-header .container-fluid {
  height: 100%;
  padding: 0 0.75rem;
}

.navbar-brand {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  margin-right: 1rem;
  display: flex;
  align-items: center;
  height: 60px;
}

.navbar-brand img {
  height: 40px;
  width: auto;
  display: block;
}

.navbar-nav .nav-link {
  height: 60px;
  display: flex;
  align-items: center;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  font-size: 1rem;
  color: #3a3b3c;
  transition: color 0.2s;
}

.navbar-nav .nav-link:hover,
.navbar-nav .nav-link:focus,
.navbar-nav .nav-link:active,
.navbar-nav .nav-link.active {
  color: var(--bs-primary) !important;
}

.navbar-toggler {
  border: none;
  padding: 0.25rem 0.75rem;
}

.navbar-toggler:focus {
  box-shadow: none;
}

.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

@media (max-width: 991.98px) {
  .mp-header {
    height: auto !important;
  }

  .navbar-nav .nav-link {
    height: auto;
    padding: 10px 0 !important;
  }
}
</style>
